import React, { FC } from 'react';
import { Table } from 'Shared/components';
import {
  Asset,
  Button,
  FilePdfIcon,
  YesIcon,
  WarningIcon,
  PaperPlaneIcon,
} from '@clearcover/atlas';
import styles from './styles.module.css';
import { useLazyApi } from '../../../Shared/services/Api';
import classNames from 'classnames';

export const PolicySignaturesRequired: FC<{
  forms: Array<SignatureRequiredFormType>;
  policyId: string;
  draftId: string;
  policyHolderEmail: string;
  displayCheckSignatureButton: boolean;
}> = ({
  forms,
  policyId,
  draftId,
  policyHolderEmail,
  displayCheckSignatureButton,
}) => {
  const [
    {
      data: deliverResult,
      loading: loadingDeliverSignableForms,
      error: deliverError,
    },
    deliverSignableForms,
  ] = useLazyApi<DeliverSignableFormsType>({
    url: `/policies/${policyId}/drafts/${draftId}/deliverSignableForms`,
    method: 'POST',
  });

  const [hasSentDelivery, changeHasChangeDelivery] = React.useState<boolean>(
    false
  );

  return (
    <>
      {forms.length > 0 && (
        <>
          <Table
            className={styles.table}
            columnWidths={['35px', 10]}
            striped={false}
            rows={forms.map(({ name }, index) => {
              return {
                key: index,
                data: [
                  <Asset
                    icon={FilePdfIcon}
                    height={16}
                    width={16}
                    description="Pdf Icon"
                  />,
                  name,
                ],
              };
            })}
          />
          <div
            className={classNames(styles.sendSignatureBar, {
              [styles.sendSignatureBarComplete]:
                deliverResult?.signatureRequestId,
            })}
          >
            <div className={styles.iconContainer}>
              <Asset
                icon={WarningIcon}
                height={16}
                width={16}
                description="Warning icon"
              />
            </div>
            <div className={styles.textContainer}>
              <span>
                Policy owner must provide an electronic signature confirming
                this change.
              </span>
              <span>
                Sending email to <u>{policyHolderEmail}</u>
              </span>

              <div className={styles.buttonContainer}>
                <Button
                  className={styles.sendSignatureBarButton}
                  data-testid="send-signature"
                  variant="secondary"
                  size="sm"
                  isSubmitting={loadingDeliverSignableForms}
                  onClick={() => {
                    deliverSignableForms().finally(() =>
                      changeHasChangeDelivery(true)
                    );
                  }}
                  disabled={
                    !!deliverResult?.signatureRequestId ||
                    loadingDeliverSignableForms
                  }
                >
                  <Asset
                    icon={PaperPlaneIcon}
                    height={16}
                    width={16}
                    description="Paper plane icon"
                  />
                  Send eSignature Email
                </Button>

                {displayCheckSignatureButton && (
                  <Button
                    className={styles.sendSignatureBarButton}
                    data-testid="check-signature-status"
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      window.open(
                        `https://app.hellosign.com/editor/view/super_group_guid/${deliverResult.signatureRequestId}`,
                        '_blank'
                      );
                    }}
                    disabled={
                      !hasSentDelivery || !deliverResult?.signatureRequestId
                    }
                  >
                    Check Signature Status
                  </Button>
                )}
              </div>
            </div>
          </div>
          <div className={styles.sendSignatureResult}>
            {hasSentDelivery && deliverResult?.signatureRequestId && (
              <span className={styles.sendSignatureResultSuccess}>
                <Asset
                  icon={YesIcon}
                  height={16}
                  width={16}
                  description="Yes icon"
                />
                eSignature email has been sent to {policyHolderEmail}
              </span>
            )}
            {hasSentDelivery && !deliverResult?.signatureRequestId && (
              <span className={styles.sendSignatureResultFailure}>
                <Asset
                  icon={WarningIcon}
                  height={16}
                  width={16}
                  description="Warning icon"
                />
                {deliverError?.response?.data?.metadata?.draftStatus
                  ? `Draft has already been ${deliverError?.response?.data?.metadata?.draftStatus.toLowerCase()} ` +
                    'so no signature packet was sent'
                  : 'Our systems are having trouble communicating right now - please try again later'}
              </span>
            )}
          </div>
        </>
      )}
    </>
  );
};

export type DeliverSignableFormsType = {
  signatureRequestId: string | undefined;
};

export type SignatureRequiredFormType = {
  name: string;
  number: string;
  edition: string;
  signatureOrder: number;
};

export type SignatureRequiredFormResponseType = {
  forms: Array<SignatureRequiredFormType>;
  useNewSignatureUI: boolean;
};
